// src/components/SalesTabs.jsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { roundQuantity } from "../utils/marbleQuantity";

// Helpers
const ymd = (d) => new Date(d).toISOString().slice(0, 10);
const treatMap = { epoxy: "Epoxy", resin: "Resin", none: "None" };

function formatVariant(dist) {
  if (!dist) return "";
  const face = treatMap[dist.face_treatment || "none"];
  const back = treatMap[dist.back_treatment || "none"];
  const polish = dist.is_polished ? " + Polished" : "";
  const suffix = dist.variant_suffix ? ` — ${dist.variant_suffix}` : "";
  return `Front ${face} / Back ${back}${polish}${suffix}`;
}

function SalesTabs({ user }) {
  const [blockDistributions, setBlockDistributions] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [sales, setSales] = useState([]);
  const [salesDetails, setSalesDetails] = useState([]);

  const [returnNotes, setReturnNotes] = useState([]);    // return_note
  const [returnDetails, setReturnDetails] = useState([]); // return_note_details

  const [loading, setLoading] = useState(true);

  // الفلاتر
  const [filterPeriod, setFilterPeriod] = useState("daily"); // daily | weekly | monthly | custom
  const [dayDate, setDayDate] = useState(() => ymd(new Date())); // يوم واحد لـ daily
  const [fromDate, setFromDate] = useState(() =>
    ymd(new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  );
  const [toDate, setToDate] = useState(() => ymd(new Date()));
  const [selectedShowroom, setSelectedShowroom] = useState("all"); // 'all' | showroom_id

  // توسيع/طي تفاصيل كل بيع
  const [expandedSales, setExpandedSales] = useState({}); // { [saleId]: true/false }

  const distById = useMemo(
    () => Object.fromEntries(blockDistributions.map((d) => [d.id, d])),
    [blockDistributions]
  );

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPeriod, dayDate, fromDate, toDate, selectedShowroom]);

  function computeRange() {
    let startDate = new Date();
    let endDate = new Date();

    if (filterPeriod === "daily") {
      const d = new Date(dayDate);
      startDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
      endDate   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    } else if (filterPeriod === "weekly") {
      const d = new Date();
      const day = d.getDay();
      startDate = new Date(d);
      startDate.setDate(d.getDate() - day);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    } else if (filterPeriod === "monthly") {
      const d = new Date();
      startDate = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    } else {
      const s = fromDate ? new Date(fromDate) : new Date();
      s.setHours(0, 0, 0, 0);
      const e = toDate ? new Date(toDate) : new Date();
      e.setHours(23, 59, 59, 999);
      startDate = s <= e ? s : e;
      endDate   = e >= s ? e : s;
    }

    return {
      startISO: startDate.toISOString(),
      endISO: endDate.toISOString(),
    };
  }

  async function fetchAllData() {
    setLoading(true);

    // 1) بيانات ثابتة
    const [
      { data: showroomsData },
      { data: blocksData },
      { data: materialsData },
      { data: customersData },
      { data: distData },
    ] = await Promise.all([
      supabase.from("showroom").select("*"),
      supabase.from("block").select("*"),
      supabase.from("material").select("*"),
      supabase.from("customer").select("id,name"),
      supabase.from("block_distribution").select("*"),
    ]);
    setShowrooms(showroomsData || []);
    setBlocks(blocksData || []);
    setMaterials(materialsData || []);
    setCustomers(customersData || []);
    setBlockDistributions(distData || []);

    // 2) نطاق التاريخ
    const { startISO, endISO } = computeRange();

    // 3) المبيعات (مع فلتر المعرض لو محدد)
    let salesQuery = supabase
      .from("sale")
      .select("*")
      .gte("date", startISO)
      .lte("date", endISO)
      .order("date", { ascending: false });

    if (selectedShowroom !== "all") {
      salesQuery = salesQuery.eq("showroom_id", Number(selectedShowroom));
    }
    const { data: salesData, error: errSales } = await salesQuery;
    if (errSales) console.error("Error fetching sales:", errSales);
    setSales(salesData || []);

    const saleIds = (salesData || []).map((s) => s.id);
    let sdData = [];
    if (saleIds.length) {
      const { data: sd, error: errSD } = await supabase
        .from("sale_details")
        .select("*")
        .in("sale_id", saleIds);
      if (errSD) console.error("Error fetching sale_details:", errSD);
      sdData = sd || [];
    }
    setSalesDetails(sdData);

    // 4) المرتجعات
    const { data: rnData, error: errRN } = await supabase
      .from("return_note")
      .select("id, date, customer_id, original_sale_id, reason, total_refund, notes")
      .gte("date", startISO)
      .lte("date", endISO)
      .order("date", { ascending: false });
    if (errRN) console.error("Error fetching return_note:", errRN);
    setReturnNotes(rnData || []);

    let rdData = [];
    if ((rnData || []).length) {
      const rnIds = rnData.map((r) => r.id);
      const { data: rdRaw, error: errRD } = await supabase
        .from("return_note_details")
        .select("*")
        .in("return_id", rnIds);
      if (errRD) console.error("Error fetching return_note_details:", errRD);
      rdData = rdRaw || [];
    }
    setReturnDetails(rdData);

    setLoading(false);
  }

  // ===== Lookups =====
  const showroomsLookup = useMemo(
    () => Object.fromEntries(showrooms.map((s) => [s.id, s.name])),
    [showrooms]
  );
  const blocksLookup = useMemo(
    () => Object.fromEntries(blocks.map((b) => [b.id, b])),
    [blocks]
  );
  const materialsLookup = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m.name || m.material_name])),
    [materials]
  );
  const customersLookup = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.id, c.name])),
    [customers]
  );
  const rnLookup = useMemo(
    () => Object.fromEntries(returnNotes.map((r) => [r.id, r])),
    [returnNotes]
  );

  function getBlockCode(block_id) {
    return blocksLookup[block_id]?.code || "";
  }
  function getMaterialNameByBlockId(block_id) {
    const b = blocksLookup[block_id];
    return b ? materialsLookup[b.material_id] || "" : "";
  }

  // ========= حساب صافي الفاتورة بعد خصم discount_value =========
  function computeSaleNet(sale, details) {
    // إجمالي بنود الفاتورة من sale_details (قبل الخصم)
    const subtotal = details.reduce((sum, d) => sum + Number(d.total_price || 0), 0);

    // الخصم النقدي من جدول sale
    const discountFixed = Number(sale.discount_value ?? 0);

    // الصافي بعد الخصم
    let totalAfter = subtotal - discountFixed;

    // لا يصبح بالسالب
    totalAfter = Math.max(0, Number(totalAfter.toFixed(2)));

    return { subtotal: Number(subtotal.toFixed(2)), totalAfter };
  }

  // ===== تجميع المبيعات لكل معرض — بالصافي بعد الخصم =====
  const salesByShowroom = useMemo(() => {
    const map = {};
    for (const sale of sales) {
      const showroomId = sale.showroom_id;
      if (!showroomId) continue;

      if (!map[showroomId]) {
        map[showroomId] = {
          showroom_id: showroomId,
          showroom_name: showroomsLookup[showroomId] || "Unknown",
          sales: [],
          totalSalesNet: 0,   // ← صافي بعد الخصم
          totalSalesGross: 0, // ← قبل الخصم (اختياري للعرض)
        };
      }

      const details = salesDetails.filter((d) => d.sale_id === sale.id);

      const { subtotal, totalAfter } = computeSaleNet(sale, details);

      map[showroomId].sales.push({
        ...sale,
        details,
        subtotal,            // قبل الخصم (مرجعي فقط)
        totalSaleNet: totalAfter, // ← الصافي بعد الخصم
      });

      map[showroomId].totalSalesNet   += totalAfter;
      map[showroomId].totalSalesGross += subtotal;
    }

    // تقريب أرقام
    Object.values(map).forEach((sh) => {
      sh.totalSalesNet   = Number(sh.totalSalesNet.toFixed(2));
      sh.totalSalesGross = Number(sh.totalSalesGross.toFixed(2));
    });

    return map;
  }, [sales, salesDetails, showroomsLookup]);

  // ===== تجميع المرتجعات لكل معرض =====
  const returnsByShowroom = useMemo(() => {
    const map = {};
    for (const rd of returnDetails) {
      const shId = rd.showroom_id;
      const noteId = rd.return_id;
      if (!shId || !noteId) continue;

      // فلتر المعرض
      if (selectedShowroom !== "all" && String(shId) !== String(selectedShowroom)) {
        continue;
      }

      if (!map[shId]) {
        map[shId] = {
          showroom_id: shId,
          showroom_name: showroomsLookup[shId] || "Unknown",
          noteGroups: {},
          totalReturns: 0,
        };
      }

      const price = Number(rd.price_per_m2 || 0);
      const area  = Number(rd.area_returned_m2 || 0);
      const line  =
        rd.line_refund_amount != null
          ? Number(rd.line_refund_amount)
          : Number((price * area).toFixed(2));

      if (!map[shId].noteGroups[noteId]) {
        const rn = rnLookup[noteId];
        map[shId].noteGroups[noteId] = {
          id: noteId,
          date: rn?.date || null,
          items: [],
          return_total: 0,
        };
      }

      map[shId].noteGroups[noteId].items.push({
        block_id: rd.block_id,
        block_code: getBlockCode(rd.block_id),
        material_name: getMaterialNameByBlockId(rd.block_id),
        slabs: Number(rd.slabs_returned || 0),
        area,
        price_per_m2: price,
        refund_total: line,
        sale_detail_id: rd.sale_detail_id,
      });

      map[shId].noteGroups[noteId].return_total += line;
      map[shId].totalReturns += line;
    }

    Object.values(map).forEach((sh) => {
      sh.returns = Object.values(sh.noteGroups).map((g) => ({
        id: g.id,
        date: g.date,
        items: g.items,
        return_total: Number(g.return_total.toFixed(2)),
      }));
      delete sh.noteGroups;
      sh.totalReturns = Number(sh.totalReturns.toFixed(2));
    });

    return map;
  }, [returnDetails, rnLookup, showroomsLookup, blocksLookup, materialsLookup, selectedShowroom]);

  // ===== ادمج المبيعات والمرتجعات لكل معرض =====
  const mergedByShowroom = useMemo(() => {
    const showroomIds = new Set([
      ...Object.keys(salesByShowroom || {}),
      ...Object.keys(returnsByShowroom || {}),
    ]);

    let result = [];
    for (const idStr of showroomIds) {
      const id = Number(idStr);
      if (selectedShowroom !== "all" && String(id) !== String(selectedShowroom)) continue;

      const salesPart = salesByShowroom[id] || {
        showroom_id: id,
        showroom_name: showroomsLookup[id] || "Unknown",
        sales: [],
        totalSalesNet: 0,
      };
      const returnsPart = returnsByShowroom[id] || {
        showroom_id: id,
        showroom_name: showroomsLookup[id] || "Unknown",
        returns: [],
        totalReturns: 0,
      };

      const totalSalesNet = Number(salesPart.totalSalesNet || 0);
      const totalReturns  = Number(returnsPart.totalReturns || 0);
      const net = Number((totalSalesNet - totalReturns).toFixed(2));

      result.push({
        showroom_id: id,
        showroom_name: salesPart.showroom_name || returnsPart.showroom_name,
        sales: salesPart.sales,
        totalSalesNet,
        returns: returnsPart.returns,
        totalReturns,
        net,
      });
    }
    return result.sort((a, b) => b.net - a.net);
  }, [salesByShowroom, returnsByShowroom, showroomsLookup, selectedShowroom]);

  // ===== إجماليات للمعروض فقط (بعد الخصم) =====
  const grand = useMemo(() => {
    const totals = mergedByShowroom.reduce(
      (acc, r) => {
        acc.sales   += Number(r.totalSalesNet || 0); // بعد الخصم
        acc.returns += Number(r.totalReturns || 0);
        acc.net     += Number(r.net || 0);
        return acc;
      },
      { sales: 0, returns: 0, net: 0 }
    );
    totals.sales   = Number(totals.sales.toFixed(2));
    totals.returns = Number(totals.returns.toFixed(2));
    totals.net     = Number(totals.net.toFixed(2));
    return totals;
  }, [mergedByShowroom]);

  if (loading) return <div className="ui-legacy-page">Loading records...</div>;

  return (
    <div className="ui-legacy-page" dir="ltr" style={{ maxWidth: 1100, margin: "auto", padding: 24, textAlign: "left" }}>
      <h2 style={{ color: "var(--accent, #514536)", marginBottom: 16 }}>Showroom sales and returns report</h2>

      {/* الفلاتر */}
      <div className="ui-toolbar" style={{ marginBottom: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div className="ui-field">
          <label style={{ marginLeft: 8 }}>Period:</label>
          <select
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}
            style={{ padding: "6px 10px" }}
          >
            <option value="daily">Today (Select a date)</option>
            <option value="weekly">This week</option>
            <option value="monthly">This month</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {filterPeriod === "daily" && (
          <div className="ui-field">
            <label style={{ marginLeft: 6 }}>Today:</label>
            <input type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)} style={{ padding: "6px 8px" }} />
          </div>
        )}

        <div className="ui-field">
          <label style={{ marginLeft: 6 }}>From:</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: "6px 8px" }} />
        </div>
        <div className="ui-field">
          <label style={{ marginLeft: 6 }}>To:</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: "6px 8px" }} />
        </div>

        <div style={{ marginRight: "auto" }} />
        <div className="ui-field">
          <label style={{ marginLeft: 8 }}>Showroom:</label>
          <select
            value={selectedShowroom}
            onChange={(e) => setSelectedShowroom(e.target.value)}
            style={{ padding: "6px 10px", minWidth: 160 }}
          >
            <option value="all">All</option>
            {showrooms.map((s) => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {mergedByShowroom.length === 0 && <div>No data for the selected period</div>}

      {mergedByShowroom.map((sh) => (
        <div key={sh.showroom_id} style={{ marginBottom: 40 }}>
          <h3 style={{ color: "var(--accent, #514536)", marginBottom: 8 }}>{sh.showroom_name}</h3>

          {/* جدول المبيعات */}
          <div style={{ fontWeight: "bold", margin: "8px 0" }}>Showroom sales</div>
          <div className="ui-table-scroll"><table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#faf8f4",
              textAlign: "left",
              direction: "ltr",
              fontSize: 14,
              marginBottom: 16,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#d2cbc1" }}>
                <th style={thTdStyle}>#</th>
                <th style={thTdStyle}>Sale number</th>
                <th style={thTdStyle}>Date</th>
                <th style={thTdStyle}>Summary</th>
                <th style={thTdStyle}>Invoice total</th>
              </tr>
            </thead>
            <tbody>
              {sh.sales.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan={5}>No sales in this period</td>
                </tr>
              ) : (
                sh.sales.map((sale, idx) => {
                  const customerName = customersLookup[sale.customer_id] || "";
                  // نجمع البنود بنفس block_id للعرض المختصر
                  const grp = {};
                  for (const d of sale.details) {
                    const k = String(d.block_id);
                    if (!grp[k]) {
                      grp[k] = {
                        block_id: d.block_id,
                        code: getBlockCode(d.block_id),
                        material_name: getMaterialNameByBlockId(d.block_id),
                        slabs: 0,
                        area: 0,
                        total: 0,
                        items: [],
                      };
                    }
                    grp[k].slabs = roundQuantity(grp[k].slabs + Number(d.slabs_sold || 0));
                    grp[k].area  += Number(d.area_sold  || 0);
                    grp[k].total += Number(d.total_price || 0);
                    grp[k].items.push(d);
                  }
                  const groups = Object.values(grp);

                  const isOpen = !!expandedSales[sale.id];
                  return (
                    <tr key={sale.id}>
                      <td style={tdStyle}>{idx + 1}</td>
                      <td style={tdStyle}>{sale.id}</td>
                      <td style={tdStyle}>{(sale.date || "").split("T")[0]}</td>
                      <td style={tdStyle}>
                        <div style={{ marginBottom: 6, color: "var(--accent, #514536)", fontWeight: "bold" }}>
                          Customer: {customerName || "-"}
                        </div>
                        {groups.map((g, i) => {
                          const uniqueTreats = Array.from(
                            new Set(
                              g.items
                                .map((it) => formatVariant(distById[it.distribution_id]))
                                .filter(Boolean)
                            )
                          ).join(", ");

                          return (
                            <div key={i} style={{ marginBottom: 4 }}>
                              Code: {g.code} — Material: {g.material_name}
                              {uniqueTreats && <> — Treatment: {uniqueTreats}</>}
                              {" — "}Total area: {g.area.toFixed(3)} m² — Total slabs: {g.slabs}
                              {" — "}Total (Before discount): {g.total.toLocaleString()} EGP
                            </div>
                          );
                        })}

                        {/* زرار التفاصيل */}
                        {sale.details.length > 0 && (
                          <button className="ui-secondary"
                            type="button"
                            onClick={() =>
                              setExpandedSales((s) => ({ ...s, [sale.id]: !s[sale.id] }))
                            }
                            style={{
                              marginTop: 6,
                              background: "#f2ede4",
                              border: "1px solid #d2cbc1",
                              borderRadius: 6,
                              padding: "4px 10px",
                              cursor: "pointer",
                              fontWeight: "bold",
                              color: "var(--accent, #514536)",
                            }}
                          >
                            {isOpen ? "Hide details" : "Show details"}
                          </button>
                        )}

                        {isOpen && (
                          <div style={{ marginTop: 8 }}>
                            <div className="ui-table-scroll"><table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                background: "#faf8f4",
                                border: "1px solid #d2cbc1",
                                fontSize: 13,
                              }}
                            >
                              <thead>
                                <tr style={{ background: "#f2ede4" }}>
                                  <th style={thInner}>Block code</th>
                                  <th style={thInner}>Treatment</th>
                                  <th style={thInner}>Width (m)</th>
                                  <th style={thInner}>Height (m)</th>
                                  <th style={thInner}>Slab count</th>
                                  <th style={thInner}>Area (m²)</th>
                                  <th style={thInner}>Price per m²</th>
                                  <th style={thInner}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sale.details.map((d, j) => (
                                  <tr key={j}>
                                    <td style={tdInner}>{getBlockCode(d.block_id)}</td>
                                    <td style={tdInner}>{formatVariant(distById[d.distribution_id]) || "-"}</td>
                                    <td style={tdInner}>{d.width ?? "-"}</td>
                                    <td style={tdInner}>{d.height ?? "-"}</td>
                                    <td style={tdInner}>{d.slabs_sold}</td>
                                    <td style={tdInner}>{d.area_sold}</td>
                                    <td style={tdInner}>{d.price_per_m2}</td>
                                    <td style={tdInner}>{d.total_price}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table></div>
                          </div>
                        )}
                      </td>

                      {/* ← الصافي بعد الخصم */}
                      <td style={tdStyle}>
                        {Number(sale.totalSaleNet || 0).toLocaleString()} EGP
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot>
              <tr>
                <td style={tdStyle} colSpan={4}><b>Total showroom sales (after discount)</b></td>
                <td style={{ ...tdStyle, fontWeight: "bold" }}>
                  {Number(sh.totalSalesNet || 0).toLocaleString()} EGP
                </td>
              </tr>
            </tfoot>
          </table></div>

          {/* جدول المرتجعات */}
          <div style={{ fontWeight: "bold", margin: "8px 0" }}>Showroom returns</div>
          <div className="ui-table-scroll"><table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#faf8f4",
              textAlign: "left",
              direction: "ltr",
              fontSize: 14,
              marginBottom: 8,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#e6dec8" }}>
                <th style={thTdStyle}>#</th>
                <th style={thTdStyle}>Return number</th>
                <th style={thTdStyle}>Return date</th>
                <th style={thTdStyle}>Return details</th>
                <th style={thTdStyle}>Total returns</th>
              </tr>
            </thead>
            <tbody>
              {!sh.returns || sh.returns.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan={5}>No returns in this period</td>
                </tr>
              ) : (
                sh.returns.map((rn, idx) => (
                  <tr key={rn.id} style={{ background: "#e6dec8" }}>
                    <td style={tdStyle}>{idx + 1}</td>
                    <td style={tdStyle}>#{rn.id}</td>
                    <td style={tdStyle}>{(rn.date || "").split("T")[0]}</td>
                    <td style={{ ...tdStyle, color: "#514536" }}>
                      {rn.items.map((it, i) => (
                        <div key={i} style={{ marginBottom: 4 }}>
                          Code: {it.block_code} | Material: {it.material_name} | Slab count: {it.slabs} | Area: {it.area} m² | Price: {it.price_per_m2} | <b>Total: -{it.refund_total}</b>
                        </div>
                      ))}
                    </td>
                    <td style={{ ...tdStyle, color: "#514536", fontWeight: "bold" }}>
                      -{Number(rn.return_total || 0).toLocaleString()} EGP
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td style={tdStyle} colSpan={4}><b>Total showroom returns</b></td>
                <td style={{ ...tdStyle, fontWeight: "bold", color: "#514536" }}>
                  -{Number(sh.totalReturns || 0).toLocaleString()} EGP
                </td>
              </tr>
            </tfoot>
          </table></div>

          {/* صافي المعرض */}
          <div style={{ fontWeight: "bold", fontSize: 15, color: "var(--accent, #514536)", margin: "6px 0 18px" }}>
            Showroom net sales (Sales after discount - Returns): {Number(sh.net || 0).toLocaleString()} EGP
          </div>

          <hr style={{ border: 0, borderTop: "1px dashed #d2cbc1", margin: "22px 0" }} />
        </div>
      ))}

      {/* إجمالي كل المعارض */}
      <div style={{ fontWeight: "bold", fontSize: 16, color: "var(--accent, #514536)", marginTop: 8, textAlign: "left" }}>
        Total sales: {grand.sales.toLocaleString()} EGP — Total returns:{" "}
        <span style={{ color: "#514536" }}>-{grand.returns.toLocaleString()}</span>{" "}
        EGP — Net: {grand.net.toLocaleString()} EGP
      </div>
    </div>
  );
}

const thTdStyle = {
  padding: 10,
  border: "1px solid #d2cbc1",
  fontWeight: "bold",
  textAlign: "left",
};

const tdStyle = {
  padding: 10,
  border: "1px solid #d2cbc1",
  textAlign: "left",
};

// جدول التفاصيل الداخلي
const thInner = {
  padding: 6,
  border: "1px solid #d2cbc1",
  fontWeight: "bold",
  textAlign: "left",
};
const tdInner = {
  padding: 6,
  border: "1px solid #d2cbc1",
  textAlign: "left",
};

export default SalesTabs;
